import React from "react";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    ChevronLeft,
    FileCode,
    Loader2,
    AlertCircle,
    ChevronRight,
    Folder,
    History,
    FileText,
    Users,
    Globe,
    Shield,
    ShieldCheck,
    Database,
    Puzzle,
    Key,
    Settings,
    Code,
    Server,
    Upload,
    Share2,
    GitBranch,
    FlaskConical,
    Activity,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

function ComponentsDashboardNav({
    dashboardSection,
    selectedRightPanel,
    folders,
    componentsByFolder,
    rootComponents,
    selectedFolder,
    selectedComponent,
    loading,
    error,
    onBackToMenu,
    onBackToEditor,
    onEnterEditor,
    onEnterOverview,
    onEnterUsers,
    onEnterDomains,
    onEnterAuthentication,
    onEnterDataSecurity,
    onEnterDatabaseSharing,
    onEnterFunctionSharing,
    onEnterIntegrations,
    onEnterSecrets,
    onEnterSettings,
    onEnterAPIs,
    onEnterEnvironments,
    onEnterImport,
    onEnterCodeScanning,
    onEnterTesting,
    onEnterActivity,
    hasApiFunctions = false,
    onFolderSelect,
    onComponentSelect,
    onViewVersionHistory,
}) {
    const { t } = useTranslation();
    const { isSuperAdmin, isFeatureEnabled } = useAuth();

    // Helper to calculate component counts recursively
    const getRecursiveComponentCount = (folderPath) => {
        return Object.keys(componentsByFolder).reduce((acc, key) => {
            if (key.startsWith(folderPath)) {
                return acc + (componentsByFolder[key]?.length || 0);
            }
            return acc;
        }, 0);
    };

    return (
        <div className="w-64 border-r bg-gray-50 flex flex-col h-full">
            <ScrollArea className="flex-1">
                {dashboardSection === "menu" ? (
                    // Main Menu View
                    <div className="p-4 pb-8">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                            {t("appPreview.navigation") || "Navigation"}
                        </h3>
                        <div className="space-y-1">
                            <button
                                onClick={onEnterOverview}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "overview"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <FileText className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.overview") || "Overview"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterUsers}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "users"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Users className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.users") || "Users"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterEditor}
                                className="w-full text-left px-3 py-3 text-sm rounded-md hover:bg-gray-200 transition-colors text-gray-700 hover:text-gray-900 flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-3">
                                    <FileCode className="w-5 h-5 text-gray-600" />
                                    <span className="font-medium">
                                        {t("appPreview.editor") || "Editor"}
                                    </span>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                            </button>
                            <button
                                onClick={onViewVersionHistory}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "version-history"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <History className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("stepsExecutionPanel.versionHistory") ||
                                        "Version History"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterDomains}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "domains"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Globe className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.domains") || "Domains"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterAuthentication}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "authentication"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Shield className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.authentication") ||
                                        "Authentication"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterDataSecurity}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "data-security"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Database className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.dataSecurity") ||
                                        "Data Security"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterCodeScanning}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "code-scanning"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <ShieldCheck className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.codeScanning") ||
                                        "Code Scanning"}
                                </span>
                            </button>
                            {isFeatureEnabled("aiTesting") && (
                                <button
                                    onClick={onEnterTesting}
                                    className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                        selectedRightPanel === "testing"
                                            ? "bg-blue-100 text-blue-900 font-medium"
                                            : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                    }`}
                                >
                                    <FlaskConical className="w-5 h-5 text-gray-600" />
                                    <span className="font-medium">
                                        {t("testing.aiTesting") || "AI Testing"}
                                    </span>
                                    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        Beta
                                    </span>
                                </button>
                            )}
                            <button
                                onClick={onEnterDatabaseSharing}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "database-sharing"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Share2 className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.databaseSharing") ||
                                        "Database Sharing"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterFunctionSharing}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "function-sharing"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <GitBranch className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.functionSharing") ||
                                        "Function Sharing"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterIntegrations}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "integrations"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Puzzle className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.integrations") ||
                                        "Integrations"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterSecrets}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "secrets"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Key className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.secrets") || "Secrets"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterSettings}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "settings"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Settings className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.settings") || "Settings"}
                                </span>
                            </button>
                            <button
                                onClick={onEnterAPIs}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "apis"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Code className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("appPreview.apis") || "APIs"}
                                </span>
                            </button>
                            {hasApiFunctions && (
                                <button
                                    onClick={onEnterActivity}
                                    className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                        selectedRightPanel === "activity"
                                            ? "bg-blue-100 text-blue-900 font-medium"
                                            : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                    }`}
                                >
                                    <Activity className="w-5 h-5 text-gray-600" />
                                    <span className="font-medium">
                                        {t("activity.title") || "Activity"}
                                    </span>
                                </button>
                            )}
                            <button
                                onClick={onEnterEnvironments}
                                className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                    selectedRightPanel === "environments"
                                        ? "bg-blue-100 text-blue-900 font-medium"
                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                }`}
                            >
                                <Server className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                    {t("environments.title") || "Environments"}
                                </span>
                            </button>
                            {/* Super Admin Only - Import Project */}
                            {isSuperAdmin && (
                                <button
                                    onClick={onEnterImport}
                                    className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors flex items-center gap-3 ${
                                        selectedRightPanel === "import"
                                            ? "bg-blue-100 text-blue-900 font-medium"
                                            : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                    }`}
                                >
                                    <Upload className="w-5 h-5 text-gray-600" />
                                    <span className="font-medium">
                                        {t("appPreview.importProject") ||
                                            "Import Project"}
                                    </span>
                                    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                        {t("appPreview.superAdminOnly") ||
                                            "Super Admin"}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                ) : dashboardSection === "editor" ? (
                    // Editor View - Root Components and Folders
                    <div className="p-4 pb-8">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBackToMenu}
                            className="mb-4 w-full justify-start"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            {t("appPreview.backToMenu") || "Back to Menu"}
                        </Button>
                        <div className="flex items-center gap-2 mb-4">
                            <FileCode className="w-5 h-5 text-gray-600" />
                            <h3 className="text-sm font-semibold text-gray-900">
                                {t("appPreview.editor") || "Editor"}
                            </h3>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                            </div>
                        ) : error ? (
                            <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
                                <AlertCircle className="w-4 h-4" />
                                {error?.message ||
                                    t("appPreview.failedToLoadComponents") ||
                                    "Failed to load components"}
                            </div>
                        ) : rootComponents.length === 0 &&
                          folders.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-8">
                                {t("appPreview.noComponentsFound") ||
                                    "No components found"}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {/* Root Level Components */}
                                {rootComponents.length > 0 && (
                                    <>
                                        {rootComponents.map((component) => (
                                            <button
                                                key={component.id}
                                                onClick={() =>
                                                    onComponentSelect(component)
                                                }
                                                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                                                    selectedComponent?.id ===
                                                    component.id
                                                        ? "bg-blue-100 text-blue-900 font-medium"
                                                        : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <FileCode className="w-4 h-4 text-gray-600" />
                                                    {component.name}
                                                </div>
                                            </button>
                                        ))}
                                        {folders.filter((f) => !f.includes("/"))
                                            .length > 0 && (
                                            <div className="border-t border-gray-200 my-2 pt-2" />
                                        )}
                                    </>
                                )}

                                {/* Top-Level Folders */}
                                {folders
                                    .filter((f) => !f.includes("/"))
                                    .map((folder) => (
                                        <button
                                            key={folder}
                                            onClick={() =>
                                                onFolderSelect(folder)
                                            }
                                            className="w-full text-left px-3 py-3 text-sm rounded-md hover:bg-gray-200 transition-colors text-gray-700 hover:text-gray-900 flex items-center justify-between group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Folder className="w-4 h-4 text-gray-600" />
                                                <span className="font-medium truncate">
                                                    {folder}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500">
                                                    {getRecursiveComponentCount(
                                                        folder
                                                    )}
                                                </span>
                                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                                            </div>
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                ) : (
                    // Component List View for a selected folder (now handles subfolders)
                    <div className="p-4 pb-8">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBackToEditor}
                            className="mb-4 w-full justify-start"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            {t("appPreview.back") || "Back"}
                        </Button>
                        <div className="flex items-center gap-2 mb-4">
                            <Folder className="w-5 h-5 text-gray-600" />
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                                {selectedFolder}
                            </h3>
                        </div>

                        {(() => {
                            const directSubfolders = [
                                ...new Set(
                                    folders
                                        .filter((f) =>
                                            f.startsWith(selectedFolder + "/")
                                        )
                                        .map(
                                            (f) =>
                                                f
                                                    .substring(
                                                        selectedFolder.length +
                                                            1
                                                    )
                                                    .split("/")[0]
                                        )
                                ),
                            ];
                            const componentsInFolder =
                                componentsByFolder[selectedFolder] || [];

                            if (
                                directSubfolders.length === 0 &&
                                componentsInFolder.length === 0
                            ) {
                                return (
                                    <div className="text-sm text-gray-500 text-center py-8">
                                        {t(
                                            "appPreview.noComponentsOrFoldersFound"
                                        ) || "No components or folders found"}
                                    </div>
                                );
                            }

                            return (
                                <div className="space-y-1">
                                    {/* Subfolders */}
                                    {directSubfolders.map((folder) => (
                                        <button
                                            key={folder}
                                            onClick={() =>
                                                onFolderSelect(
                                                    `${selectedFolder}/${folder}`
                                                )
                                            }
                                            className="w-full text-left px-3 py-3 text-sm rounded-md hover:bg-gray-200 transition-colors text-gray-700 hover:text-gray-900 flex items-center justify-between group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Folder className="w-4 h-4 text-gray-600" />
                                                <span className="font-medium truncate">
                                                    {folder}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500">
                                                    {getRecursiveComponentCount(
                                                        `${selectedFolder}/${folder}`
                                                    )}
                                                </span>
                                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                                            </div>
                                        </button>
                                    ))}

                                    {/* Separator if both folders and components exist */}
                                    {directSubfolders.length > 0 &&
                                        componentsInFolder.length > 0 && (
                                            <div className="border-t border-gray-200 my-2 pt-2" />
                                        )}

                                    {/* Components in the current folder */}
                                    {componentsInFolder.map((component) => (
                                        <button
                                            key={component.id}
                                            onClick={() =>
                                                onComponentSelect(component)
                                            }
                                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                                                selectedComponent?.id ===
                                                component.id
                                                    ? "bg-blue-100 text-blue-900 font-medium"
                                                    : "hover:bg-gray-200 text-gray-700 hover:text-gray-900"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <FileCode className="w-4 h-4 text-gray-600" />
                                                {component.name}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

export default ComponentsDashboardNav;
